import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/AfterLogin.css"; // Import styles

const keycloakTokenEndpoint = "https://ncjp.psanalytics.co.uk/auth/realms/ncjp/protocol/openid-connect/token";
const clientId = "ncjp-app";
const clientSecret = "1ZtA2aRuuAt9RMMVeNTnPPJXP5p9Eo5q"; // If required
const redirectUri = "https://ncjp.psanalytics.co.uk/after-login";
const registerApiEndpoint = "https://api.psanalytics.co.uk/ncjp/register.php";
const validateApiEndpoint = "https://api.psanalytics.co.uk/ncjp/validateInviteWithOTP.php";

interface TokenResponse {
  [key: string]: any;
}

const AfterLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const params = new URLSearchParams(location.search);
  const inviteToken = params.get("state");
  const authCode = params.get("code");

  useEffect(() => {
    console.log("Extracted Invite Token from URL:", inviteToken);
    console.log("Extracted Auth Code from URL:", authCode);
    console.log("URL Params:", params.toString());

    if (!authCode || !inviteToken) {
      console.error("Missing authorization code or invite token!");
      navigate("/invite?error=missing_token");
      return;
    }
  }, [authCode, inviteToken, navigate, params]);

  const handleValidation = async () => {
    setIsProcessing(true);
    setErrorMessage("");

    const requestBody = JSON.stringify({
      inviteToken,
      otp,
      dateOfBirth,
    });

    console.log("Sending request to validateInviteWithOTP.php:", requestBody);

    try {
      const validateResponse = await fetch(validateApiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: requestBody,
      });

      const validateData = await validateResponse.json();
      console.log("Validation Response:", validateData);

      if (!validateResponse.ok || !validateData.personID) {
        throw new Error(validateData.message || "Validation failed");
      }

      const personID = validateData.personID;
      localStorage.setItem("personID", personID);
      console.log("Validation successful. PersonID:", personID);

      fetchToken(personID);
    } catch (error: any) {
      console.error("Error during validation:", error);
      setErrorMessage(error.message || "An error occurred");
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchToken = async (personID: string) => {
    try {
      console.log("Fetching Keycloak token...");
      const response = await fetch(keycloakTokenEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          client_id: clientId,
          client_secret: clientSecret,
          grant_type: "authorization_code",
          redirect_uri: redirectUri,
          code: authCode!,
          scope: "openid email profile",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to exchange code for token");
      }

      const data: TokenResponse = await response.json();

      // Echo all token values to the console with their names.
      console.log("Keycloak Token Response:");
      Object.entries(data).forEach(([key, value]) => {
        console.log(`${key}: ${value}`);
      });

      const { access_token, id_token } = data;
      // Decode the access_token to extract the user ID (sub)
      const payload = JSON.parse(atob(access_token.split(".")[1]));
      const oneLoginSub = payload.sub;
      console.log("Authenticated User Sub:", oneLoginSub);

      // Store authentication details
      localStorage.setItem("token", access_token);
      localStorage.setItem("userId", oneLoginSub);
      localStorage.setItem("oneLoginSub", oneLoginSub);
      if (id_token) {
        localStorage.setItem("id_token", id_token);
        console.log("id_token saved to localStorage.");
      } else {
        console.warn("No id_token received in token response.");
      }

      console.log("Registering User in Database...");
      const registerResponse = await fetch(registerApiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ personID, oneLoginSub }),
      });

      const registerData = await registerResponse.json();
      console.log("Register API Response:", registerData);

      if (!registerResponse.ok) {
        throw new Error(registerData.message || "Failed to register user");
      }

      console.log("User registration successful!");
      navigate("/");
    } catch (error) {
      console.error("Error during authentication:", error);
      navigate("/invite");
    }
  };

  return (
    <div className="invite-container">
      <img src="/assets/crests/puk-logo.png" alt="PUK Logo" className="invite-logo" />

      <div className="invite-content">
        <h1 className="govuk-heading-l">National My Police Portal</h1>
        <p className="govuk-body">
          Please enter your <strong>One-Time Passcode (OTP)</strong> and <strong>Date of Birth</strong> to verify your identity.
        </p>

        {errorMessage && (
          <div className="govuk-error-summary">
            <div role="alert">
              <h2 className="govuk-error-summary__title">There was a problem</h2>
              <p className="govuk-error-summary__body">{errorMessage}</p>
            </div>
          </div>
        )}

        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="otp">One-Time Passcode (OTP)</label>
          <input className="govuk-input" type="text" id="otp" value={otp} onChange={(e) => setOtp(e.target.value)} />
        </div>

        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="dateOfBirth">Date of Birth</label>
          <input className="govuk-input" type="date" id="dateOfBirth" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
        </div>

        <div className="govuk-button-group">
          <button className="govuk-button" onClick={handleValidation} disabled={isProcessing}>
            {isProcessing ? "Processing..." : "Verify and Continue"}
          </button>
          <button className="govuk-button govuk-button--warning" type="button" onClick={() => navigate("/invite?error=cancelled")}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AfterLogin;
